import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, combineLatest, distinctUntilChanged, filter, map } from 'rxjs';

import { User } from '@tsq-web/users';

import { getEnvironment } from '../../../shared/environment';
import { CommunitiesEnvironments } from '../../../shared/environment/models/communities-environments.enum';
import { ResidentInviteStatus } from '../../models/resident-invite-status.enum';
import { PropertiesTypeaheadService } from '../../services/properties-typeahead.service';
import { NeighborInviteRegisterStore } from '../../state/neighbor-invite-register.store';
import { PropertiesStore } from '../../state/properties.store';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NeighborInviteRegisterStore, PropertiesStore, PropertiesTypeaheadService],
  template: `
    <img
      class="h-40"
      [src]="logoUrl"
      [attr.alt]="'INVITE_NEIGHBORS_REGISTER.ALT_LOGO' | translate"
    />

    <div class="border-1 border-petro-n2 mt-48 w-[504px] rounded-2xl bg-white px-40 pb-40 pt-48">
      <comm-neighbor-invite-register-sign-up-form
        *ngIf="showForm$ | async; else info"
        [isLoading]="isLoadingInvite$ | async"
        [isSigningUp]="isSigningUp$ | async"
        [properties]="properties$ | async"
        [initialEmail]="initialEmail$ | async"
        (signUp)="signUpSubject.next($event)"
      />
      <ng-template #info>
        <comm-neighbor-invite-register-info-card
          [title]="infoTitle$ | async"
          [description]="infoDescription$ | async"
        />
      </ng-template>
    </div>
  `,
  styles: [
    `
      :host {
        @apply bg-petro-n1 font-base flex min-h-screen flex-col items-center py-72;
      }
    `,
  ],
})
export class MainNeighborInviteRegisterComponent implements OnInit {
  private readonly inviteStore = inject(NeighborInviteRegisterStore);
  private readonly propertiesStore = inject(PropertiesStore);
  private readonly activatedRoute = inject(ActivatedRoute);

  protected readonly properties$ = this.propertiesStore.properties$;

  protected readonly invite$ = this.inviteStore.invite$;
  protected readonly initialEmail$ = this.inviteStore.invite$.pipe(map(invite => invite?.email));
  protected readonly isLoadingInvite$ = this.inviteStore.inviteFetchStatus$.pipe(
    map(status => status === 'loading'),
  );
  protected readonly isSigningUp$ = this.inviteStore.signUpStatus$.pipe(
    map(status => status === 'loading'),
  );

  protected readonly showForm$ = combineLatest([
    this.inviteStore.inviteFetchStatus$,
    this.inviteStore.signUpStatus$,
  ]).pipe(
    map(
      ([inviteFetchStatus, signUpStatus]) =>
        inviteFetchStatus !== 'error' && signUpStatus !== 'success',
    ),
  );

  protected readonly infoTitle$ = this.inviteStore.signUpStatus$.pipe(
    map(status =>
      status === 'success'
        ? 'INVITE_NEIGHBORS_REGISTER.SIGN_UP_SUCCESSFUL'
        : 'INVITE_NEIGHBORS_REGISTER.GENERAL_ERROR_TITLE',
    ),
  );
  protected readonly infoDescription$ = combineLatest([
    this.inviteStore.signUpStatus$,
    this.invite$,
  ]).pipe(
    map(([signUpStatus, invite]) => {
      const signUpSuccessMessage = signUpStatus === 'success' && 'SIGN_UP_SUCCESSFUL_DETAIL';
      const fetchErrorMessage = !!invite ? 'INVITE_USED' : 'GENERAL_ERROR_DETAIL';

      return `INVITE_NEIGHBORS_REGISTER.${signUpSuccessMessage || fetchErrorMessage}`;
    }),
  );

  protected readonly logoUrl =
    getEnvironment().env === CommunitiesEnvironments.BR_PROD
      ? '/assets/brand/logo.svg'
      : '/assets/brand/logo-community.svg';

  protected readonly signUpSubject = new Subject<User>();

  ngOnInit(): void {
    this.inviteStore.fetchInviteById(this.activatedRoute.snapshot.params.inviteId);

    this.listenToInviteLoad();
    this.listenToSignUp();
  }

  private listenToInviteLoad(): void {
    this.invite$
      .pipe(
        untilDestroyed(this),
        filter(invite => invite?.status === ResidentInviteStatus.Sent),
        map(invite => invite.condoId),
        distinctUntilChanged(),
      )
      .subscribe(condoId => this.propertiesStore.fetchPropertiesByCondoId(condoId));
  }

  private listenToSignUp(): void {
    combineLatest([this.inviteStore.invite$, this.signUpSubject.asObservable()])
      .pipe(untilDestroyed(this))
      .subscribe(([invite, user]) => {
        const newInvite = invite;
        newInvite.invitee = user;

        this.inviteStore.signUp(newInvite);
      });
  }
}
